import { Suspense, lazy, useState, useEffect, ReactNode, startTransition } from 'react';
import { I18nextProvider } from 'react-i18next';
import i18n from '@/i18n';
import { SafeAuthProvider } from '@/components/SafeAuthProvider';
import { OptimizedQueryProvider } from '@/contexts/OptimizedQueryContext';
import { Toaster } from '@/components/ui/sonner';

// Lazy load heavy providers - will load AFTER first paint
const DatabaseThemeProvider = lazy(() => 
  import('@/components/admin/DatabaseThemeProvider').then(m => ({ 
    default: m.DatabaseThemeProvider 
  }))
);

interface OptimizedProvidersProps {
  children: ReactNode;
}

export const OptimizedProviders = ({ children }: OptimizedProvidersProps) => {
  const [heavyProvidersReady, setHeavyProvidersReady] = useState(false);

  useEffect(() => {
    const run = () => startTransition(() => setHeavyProvidersReady(true));
    const g = globalThis as any;

    if (typeof g.requestIdleCallback === 'function') {
      const id = g.requestIdleCallback(run, { timeout: 800 });
      return () => g.cancelIdleCallback?.(id);
    }

    const timer = setTimeout(run, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <OptimizedQueryProvider>
        <SafeAuthProvider>
          {heavyProvidersReady ? (
            <Suspense fallback={children}>
              <DatabaseThemeProvider>
                {children}
              </DatabaseThemeProvider>
            </Suspense>
          ) : (
            children
          )}
          <Toaster />
        </SafeAuthProvider>
      </OptimizedQueryProvider>
    </I18nextProvider>
  );
};
